import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../config/api';
import AreaOptions from '../components/AreaOptions';
import Pagination, { DEFAULT_PAGE_SIZE, pagingParams } from '../components/Pagination';
import { apiFetch } from '../utils/session';
import ProfileDetails from '../components/ProfileDetails';
import AuthImage from '../components/AuthImage';
import SelfieImage from '../components/SelfieImage';
import './AdminMembers.css';

const STATUS_TABS = ['All', 'Pending', 'Active', 'Rejected'];
const ROLES = ['Volunteer', 'Area Head', 'Admin'];
const STATUS_LABEL = { Pending: 'Pending', Active: 'Approved', Rejected: 'Rejected' };

async function requestJson(path, options = {}) {
  const res = await apiFetch(apiUrl(path), {
    ...options,
    headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

function StatusBadge({ status }) {
  return <span className={`am-badge ${status.toLowerCase()}`}>{STATUS_LABEL[status] || status}</span>;
}

function MemberModal({ userId, onClose, onChanged }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState('');
  const [zone, setZone] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await requestJson(`/admin/users/${userId}`);
      if (cancelled) return;
      if (!ok) {
        setError(data.error || 'Could not load member');
        return;
      }
      setUser(data.user);
      setRole(data.user.role);
      setZone(data.user.assignedZone || '');
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !editing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, editing]);

  const applyResult = ({ ok, data }, fallback) => {
    if (!ok) {
      setError(data.error || fallback);
      return false;
    }
    setUser(data.user);
    setRole(data.user.role);
    setZone(data.user.assignedZone || '');
    onChanged();
    return true;
  };

  const setStatus = async (status) => {
    setBusy(true);
    setError('');
    const result = await requestJson(`/admin/users/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    applyResult(result, 'Could not update status');
    setBusy(false);
  };

  const saveRole = async () => {
    setBusy(true);
    setError('');
    const result = await requestJson(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ role, assignedZone: zone }),
    });
    applyResult(result, 'Could not update role');
    setBusy(false);
  };

  const saveDetails = async (values) => {
    const result = await requestJson(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(values),
    });
    if (!result.ok) return { error: result.data.error || 'Could not save', errors: result.data.errors };
    applyResult(result, '');
    setEditing(false);
    return null;
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete ${user.name}? This cannot be undone.`)) return;
    setBusy(true);
    setError('');
    const { ok, data } = await requestJson(`/admin/users/${userId}`, { method: 'DELETE' });
    setBusy(false);
    if (!ok) {
      setError(data.error || 'Could not delete member');
      return;
    }
    onChanged();
    onClose();
  };

  const roleDirty = user && (role !== user.role || zone !== (user.assignedZone || ''));

  return (
    <div
      className="am-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !editing) onClose();
      }}
    >
      <div className="am-modal" role="dialog" aria-modal="true" aria-labelledby="am-modal-title">
        <div className="am-modal-head">
          <div>
            <h2 id="am-modal-title" className="am-modal-title">
              {user ? user.name : 'Member'}
            </h2>
            {user ? (
              <p className="am-modal-meta">
                {user.email} · {user.memberId || user.referenceNumber || 'No ID'}
              </p>
            ) : null}
          </div>
          <button type="button" className="am-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {error ? (
          <p className="am-error" role="alert">
            {error}
          </p>
        ) : null}

        {!user && !error ? <p className="am-muted">Loading…</p> : null}

        {user ? (
          <>
            <div className="am-panels">
              <section className="am-panel">
                <h3 className="am-panel-title">Status</h3>
                <StatusBadge status={user.status} />
                <div className="am-btn-row">
                  {user.status !== 'Active' ? (
                    <button
                      type="button"
                      className="am-btn approve"
                      onClick={() => setStatus('Active')}
                      disabled={busy}
                    >
                      Approve
                    </button>
                  ) : null}
                  {user.status !== 'Rejected' ? (
                    <button
                      type="button"
                      className="am-btn reject"
                      onClick={() => setStatus('Rejected')}
                      disabled={busy}
                    >
                      Reject
                    </button>
                  ) : null}
                  {user.status !== 'Pending' ? (
                    <button
                      type="button"
                      className="am-btn ghost"
                      onClick={() => setStatus('Pending')}
                      disabled={busy}
                    >
                      Move to pending
                    </button>
                  ) : null}
                </div>
              </section>

              <section className="am-panel">
                <h3 className="am-panel-title">Role</h3>
                <label className="am-field">
                  Role
                  <select value={role} onChange={(e) => setRole(e.target.value)}>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </label>
                {role === 'Area Head' ? (
                  <label className="am-field">
                    Assigned zone
                    <select value={zone} onChange={(e) => setZone(e.target.value)}>
                      <option value="">Same as home area</option>
                      <AreaOptions current={zone} />
                    </select>
                  </label>
                ) : null}
                <button
                  type="button"
                  className="am-btn ghost"
                  onClick={saveRole}
                  disabled={busy || !roleDirty}
                >
                  Save role
                </button>
              </section>
            </div>

            <section className="am-panel">
              <h3 className="am-panel-title">Documents</h3>
              <div className="am-docs">
                <figure>
                  <figcaption>Profile photo</figcaption>
                  {user.selfiePath ? (
                    <AuthImage path={`/users/${user.id}/selfie`} alt="Profile" linkToFull />
                  ) : (
                    <span className="am-muted">None</span>
                  )}
                </figure>
                <figure>
                  <figcaption>Aadhaar</figcaption>
                  {user.aadhaarDocumentPath ? (
                    <AuthImage path={`/admin/users/${user.id}/aadhaar`} alt="Aadhaar document" linkToFull />
                  ) : (
                    <span className="am-muted">None</span>
                  )}
                </figure>
              </div>
            </section>

            <div className="am-details-head">
              <h3 className="am-panel-title">Personal details</h3>
              {!editing ? (
                <button type="button" className="am-btn ghost" onClick={() => setEditing(true)}>
                  Edit details
                </button>
              ) : null}
            </div>
            <ProfileDetails
              profile={user}
              editing={editing}
              onSave={saveDetails}
              onCancel={() => setEditing(false)}
            />

            <div className="am-danger">
              <button type="button" className="am-btn reject" onClick={remove} disabled={busy}>
                Delete member
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function AdminMembers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('All');
  const [role, setRole] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  // Debounce typing in the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setError('');
    const params = new URLSearchParams(pagingParams(page, pageSize));
    if (status !== 'All') params.set('status', status);
    if (role) params.set('role', role);
    if (query) params.set('q', query);
    const { ok, data } = await requestJson(`/admin/users?${params.toString()}`);
    if (!ok) setError(data.error || 'Could not load members');
    else {
      setUsers(data.users || []);
      setTotal(data.total ?? (data.users || []).length);
    }
    setLoading(false);
  }, [status, role, query, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const closeModal = useCallback(() => setSelectedId(null), []);

  return (
    <div className="am-page">
      <div className="am-shell">
        <header className="am-header">
          <div>
            <h1 className="am-title">Manage members</h1>
            <p className="am-sub">View, approve, edit and manage every account</p>
          </div>
          <div className="am-header-actions">
            <Link className="am-link" to="/admin">
              ← Dashboard
            </Link>
          </div>
        </header>

        <div className="am-toolbar">
          <div className="am-tabs" role="tablist">
            {STATUS_TABS.map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={status === s}
                className={`am-tab${status === s ? ' on' : ''}`}
                onClick={() => {
                  setStatus(s);
                  setPage(1);
                }}
              >
                {s === 'Active' ? 'Approved' : s}
              </button>
            ))}
          </div>
          <div className="am-filters">
            <input
              className="am-search"
              type="search"
              placeholder="Search name, email, phone, ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="am-select"
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All roles</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error ? (
          <p className="am-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="am-card">
          {loading ? (
            <p className="am-muted">Loading…</p>
          ) : users.length === 0 ? (
            <p className="am-muted">No members match these filters.</p>
          ) : (
            <div className="am-table-wrap">
              <table className="am-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Phone</th>
                    <th>Area</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>ID</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u._id}
                      className="am-row"
                      tabIndex={0}
                      onClick={() => setSelectedId(u._id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedId(u._id);
                        }
                      }}
                    >
                      <td>
                        <div className="am-member">
                          <SelfieImage
                            userId={u._id}
                            hasPhoto={Boolean(u.selfiePath)}
                            alt=""
                            className="am-thumb"
                            placeholder={<span className="am-thumb placeholder">{u.name?.[0] || '?'}</span>}
                          />
                          <span>
                            <span className="am-member-name">{u.name}</span>
                            <span className="am-member-email">{u.email}</span>
                          </span>
                        </div>
                      </td>
                      <td>{u.phone || '—'}</td>
                      <td>{u.area || '—'}</td>
                      <td>{u.role}</td>
                      <td>
                        <StatusBadge status={u.status} />
                      </td>
                      <td className="am-mono">{u.memberId || u.referenceNumber || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading ? (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          ) : null}
        </div>
      </div>

      {selectedId ? <MemberModal userId={selectedId} onClose={closeModal} onChanged={load} /> : null}
    </div>
  );
}

export default AdminMembers;
